"use client";

import React from "react";
import { Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function JoinTheCircleOfCare() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="join-the-circle-of-care" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      <div ref={ref} className={cn("container mx-auto max-w-xl relative z-10 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-white">
            Stay Informed, Anonymously.
          </h2>
          <p className="text-white/70 text-lg font-light">
            Join our community for evidence-based dental insights.
          </p>
        </div>

        <div className="bg-card/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10 shadow-2xl">
          <FormProvider formId="join_care_circle_home" sectionName="join-the-circle-of-care" defaultValues={{
          first_name: "",
          last_name: "",
          email: "",
          interests: [],
          agree: false
        }} validationRules={{
          first_name: {
            required: true
          },
          last_name: {
            required: false
          },
          email: {
            required: true,
            pattern: "email"
          },
          interests: {
            required: false
          },
          agree: {
            required: true
          }
        }} submitText="Subscribe" successMessage="Welcome to the circle. You've successfully subscribed." errorMessage="Something went wrong. Please try again.">
            <SubscriptionForm />
          </FormProvider>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-white/40 uppercase tracking-widest font-medium">
            No ads. No clinical spam. Just science.
          </p>
        </div>
      </div>

      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl pointer-events-none opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-[120px]" />
      </div>
    </section>;
}
function SubscriptionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 text-white mb-4">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif text-white">You're In.</h3>
        <p className="text-white/70">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full border-white/20 text-white hover:bg-white hover:text-black transition-colors">
          Reset Form
        </Button>
      </div>;
  }
  const inputStyles = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-white placeholder:text-white/30 text-white h-12 transition-colors";
  const interestOptions = ["Oral Hygiene", "Dental Procedures", "Myth Busting", "Kids Dental Care"];
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/80">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" {...field} className={inputStyles} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/80">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} className={inputStyles} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/80">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className={inputStyles} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/80 mb-3 block">
              Topics of Interest
            </FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {interestOptions.map(option => <label key={option} className="flex items-center space-x-3 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input type="checkbox" className="peer appearance-none w-5 h-5 border border-white/30 rounded-md checked:bg-white checked:border-white transition-all cursor-pointer" checked={field.value?.includes(option)} onChange={e => {
              const checked = e.target.checked;
              const current = field.value || [];
              if (checked) {
                field.onChange([...current, option]);
              } else {
                field.onChange(current.filter((v: string) => v !== option));
              }
            }} />
                    <Check className="w-3.5 h-3.5 text-black absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" />
                  </div>
                  <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                    {option}
                  </span>
                </label>)}
            </div>
            <FormDescription className="text-white/40 text-xs mt-2">
              Select any topics you’d like to receive updates about.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree" rules={{
      required: "You must agree to receive updates"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4 border border-white/10 bg-white/5">
            <FormControl>
              <div className="relative flex items-center mt-1">
                <input type="checkbox" className="peer appearance-none w-5 h-5 border border-white/30 rounded-md checked:bg-white checked:border-white transition-all cursor-pointer" checked={field.value} onChange={field.onChange} />
                <Check className="w-3.5 h-3.5 text-black absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" />
              </div>
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-white/90 font-normal">
                I agree to receive occasional newsletters and updates
              </FormLabel>
              <FormDescription className="text-white/50 text-xs">
                You can unsubscribe at any time.
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-200 text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-center">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] rounded-full bg-white text-slate-950 hover:bg-transparent hover:text-white hover:border hover:border-white border border-transparent transition-all h-12 px-8 text-base font-medium">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-5 w-5" />
              Subscribing...
            </> : "Subscribe"}
        </Button>
      </div>
    </Form>;
}